import to from 'await-to-js';
import { ClientCredentials } from '@/types/ClientCredentials.types';
import ContensisUris from '../ContensisUris';
import { CookieHelper } from './CookieHelper';

const LOGIN_COOKIE = 'ContensisCMSUserName';
const REFRESH_TOKEN_COOKIE = 'RefreshToken';

class CredentialStore implements ClientCredentials {
  bearerToken!: string;
  bearerTokenExpiryDate!: string;
  refreshToken?: string;
  refreshTokenExpiryDate?: string;
  contensisClassicToken?: string;

  constructor() {
    this.refresh();
  }

  refresh = () => {
    this.bearerToken = this.bearerToken || '';
    this.bearerTokenExpiryDate =
      this.bearerTokenExpiryDate || new Date(0).toUTCString();
    this.refreshToken = CookieHelper.GetCookie(REFRESH_TOKEN_COOKIE);
    this.refreshTokenExpiryDate =
      this.refreshTokenExpiryDate || new Date(0).toUTCString();
    this.contensisClassicToken = CookieHelper.GetCookie(LOGIN_COOKIE);

    if (!this.bearerToken && !this.contensisClassicToken && !this.refreshToken)
      return null;
    return this;
  };

  toJSON = (): ClientCredentials => {
    const {
      bearerToken,
      bearerTokenExpiryDate,
      refreshToken,
      refreshTokenExpiryDate,
      contensisClassicToken,
    } = this;
    return {
      bearerToken,
      bearerTokenExpiryDate,
      refreshToken,
      refreshTokenExpiryDate,
      contensisClassicToken,
    };
  };

  persistCredential = ({
    contensisClassicToken,
    refreshToken,
  }: Partial<ClientCredentials>) => {
    if (contensisClassicToken)
      CookieHelper.SetCookie(LOGIN_COOKIE, contensisClassicToken);
    if (refreshToken)
      CookieHelper.SetCookie(REFRESH_TOKEN_COOKIE, refreshToken);

    if (contensisClassicToken || refreshToken) this.refresh();
  };

  clearPersisted = () => {
    CookieHelper.DeleteCookie(LOGIN_COOKIE);
    CookieHelper.DeleteCookie(REFRESH_TOKEN_COOKIE);
  };

  getRefreshTokenFromSecurityToken = async (
    securityToken: string
  ): Promise<[Error | undefined, string | undefined]> => {
    const [error, response] = await to(
      fetch(`${ContensisUris.cms}/REST/Contensis/Security/IsAuthenticated`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          securityToken: encodeURIComponent(securityToken),
        }),
      })
    );
    if (error)
      return [
        new Error(
          `Failed to fetch a Contensis refresh token
          The error was: ${error.message}`
        ),
        undefined,
      ];
    if (response.ok) {
      const [parseError, body] = await to(response.json());
      if (parseError) return [parseError, undefined];

      const { LogonResult, ApplicationData: data = [] } = body;
      if (LogonResult !== 0) {
        return [
          new Error(
            `Failed to fetch a Contensis refresh token
            Security token is invalid: ${data}`
          ),
          undefined,
        ];
      }
      if (data.length > 0) {
        let refreshToken;
        for (const item of data) {
          if (item.Key === 'ContensisSecurityRefreshToken')
            refreshToken = item.Value;
        }
        if (!refreshToken) {
          return [
            new Error(
              'Failed to fetch a Contensis refresh token: Unable to find ContensisSecurityRefreshToken'
            ),
            undefined,
          ];
        }
        // We have a refreshToken
        return [undefined, refreshToken];
      } else {
        return [
          new Error(
            'Failed to fetch a Contensis refresh token: Unable to find ContensisSecurityRefreshToken'
          ),
          undefined,
        ];
      }
    } else {
      return [
        new Error(
          `Fetch credentials error: ${response.status} ${response.statusText}`
        ),
        undefined,
      ];
    }
  };
}
export default CredentialStore;
